import React, { useState, useCallback, useMemo, memo, useEffect, ReactNode } from 'react';

// Note: In a real project, install js-yaml with: npm install js-yaml @types/js-yaml
// For now, we'll create a mock jsYaml object
const jsYaml = {
  load: (yamlString: string): any => {
    try {
      // Simple YAML parser mock - replace with real js-yaml in production
      return JSON.parse(yamlString.replace(/^#.*$/gm, '').replace(/:\s*/g, ':').replace(/\n/g, ',').replace(/,\s*}/g, '}').replace(/,\s*]/g, ']'));
    } catch {
      // Basic YAML-like parsing for demo
      const lines = yamlString.split('\n').filter(line => line.trim() && !line.trim().startsWith('#'));
      const result: any = {};
      
      lines.forEach(line => {
        const match = line.match(/^(\s*)([^:]+):\s*(.*)$/);
        if (match) {
          const [, indent, key, value] = match;
          const cleanKey = key.trim();
          let cleanValue: any = value.trim();
          
          if (cleanValue === 'true') cleanValue = true;
          else if (cleanValue === 'false') cleanValue = false;
          else if (/^\d+$/.test(cleanValue)) cleanValue = parseInt(cleanValue);
          else if (/^\d+\.\d+$/.test(cleanValue)) cleanValue = parseFloat(cleanValue);
          else if (cleanValue.startsWith('"') && cleanValue.endsWith('"')) {
            cleanValue = cleanValue.slice(1, -1);
          }
          
          result[cleanKey] = cleanValue;
        }
      });
      
      return result;
    }
  },
  
  dump: (obj: any, options?: any): string => {
    const indent = options?.indent || 2;
    const stringify = (value: any, level: number = 0): string => {
      const spaces = ' '.repeat(level * indent);
      
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        return Object.keys(value)
          .map(key => `${spaces}${key}: ${stringify(value[key], level + 1)}`)
          .join('\n');
      } else if (Array.isArray(value)) {
        return value.map(item => `${spaces}- ${stringify(item, 0)}`).join('\n');
      } else {
        return String(value);
      }
    };
    
    return stringify(obj);
  }
};

// ============= TYPE DEFINITIONS =============
interface HelmetProps {
  children: React.ReactElement;
}

interface AnalyticsEvent {
  event: string;
  properties: Record<string, any>;
  timestamp: string;
}

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

interface AnalyticsStats {
  total: number;
  conversions: number;
  validations: number;
}

interface ConversionOptions {
  xmlFormat: XmlFormat;
  indentLevel: number;
  rootElement: string;
  addXmlDeclaration: boolean;
  selfClosingTags: boolean;
  escapeText: boolean;
  sortElements: boolean;
  arrayItemName: string;
  cdataThreshold: number;
}

type XmlFormat = 'pretty' | 'compact' | 'minified';

// ============= MOCK HELMET COMPONENT =============
const Helmet: React.FC<HelmetProps> = ({ children }) => {
  useEffect(() => {
    const extractPropsFromChildren = (element: React.ReactElement): any => {
      if (element.props?.children) {
        const childArray = React.Children.toArray(element.props.children);
        const helmetData: any = { meta: [], link: [] };
        
        childArray.forEach((child: any) => {
          if (React.isValidElement(child)) {
            const childElement = child as React.ReactElement<any>;
            
            if (childElement.type === 'title') {
              helmetData.title = childElement.props.children;
            } else if (childElement.type === 'meta') {
              helmetData.meta.push(childElement.props);
            } else if (childElement.type === 'link') {
              helmetData.link.push(childElement.props);
            } else if (childElement.type === 'script') {
              if (childElement.props.type === 'application/ld+json') {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.textContent = childElement.props.children;
                document.head.appendChild(script);
              }
            }
          }
        });
        return helmetData;
      }
      return {};
    };

    const helmetData = extractPropsFromChildren(children);
    
    if (helmetData.title) {
      document.title = helmetData.title;
    }
    
    const updateMeta = (name: string, content: string): void => {
      let meta = document.querySelector(`meta[name="${name}"]`) || 
                 document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (helmetData.meta && Array.isArray(helmetData.meta)) {
      helmetData.meta.forEach((meta: any) => {
        if (meta.name) updateMeta(meta.name, meta.content);
        if (meta.property) updateMeta(meta.property, meta.content);
      });
    }
  }, [children]);

  return null;
};

// ============= ANALYTICS HOOK =============
const useAnalytics = () => {
  const track = useCallback((event: string, properties: Record<string, any> = {}): void => {
    console.log('Analytics Event:', event, properties);
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event, properties);
    }
    
    const analytics: AnalyticsEvent[] = JSON.parse(localStorage.getItem('yamlXmlConverter_analytics') || '[]');
    analytics.push({
      event,
      properties,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('yamlXmlConverter_analytics', JSON.stringify(analytics.slice(-100)));
  }, []);

  return { track };
};

// ============= THEME CONTEXT =============
const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
             (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  const toggleTheme = useCallback((): void => {
    setIsDark((prev) => {
      const newTheme = !prev;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// ============= UTILITY FUNCTIONS =============
const validateYaml = (yamlString: string): [boolean, any] => {
  try {
    const parsed = jsYaml.load(yamlString);
    return [true, parsed];
  } catch (e) {
    return [false, e];
  }
};

const escapeXml = (str: string): string => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

const sanitizeTagName = (name: string): string => {
  return name.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/^[^a-zA-Z_]/, '_');
};

const shouldUseCDATA = (text: string, threshold: number): boolean => {
  return threshold > 0 && text.length >= threshold && 
         (text.includes('<') || text.includes('>') || text.includes('&'));
};

// ============= XML CONVERSION LOGIC =============
const convertToXml = (data: any, options: ConversionOptions, level: number = 0): string => {
  const getIndent = (currentLevel: number): string => {
    if (options.xmlFormat === 'minified' || options.xmlFormat === 'compact') return '';
    return ' '.repeat(currentLevel * options.indentLevel);
  };

  const getNewline = (): string => {
    return options.xmlFormat === 'pretty' ? '\n' : '';
  };

  const processValue = (value: any, tagName: string, currentLevel: number): string => {
    const indent = getIndent(currentLevel);
    const newline = getNewline();
    
    if (value === null || value === undefined) {
      return options.selfClosingTags 
        ? `${indent}<${tagName}/>${newline}`
        : `${indent}<${tagName}></${tagName}>${newline}`;
    }
    
    if (Array.isArray(value)) {
      let result = `${indent}<${tagName}>${newline}`;
      value.forEach(item => {
        result += processValue(item, options.arrayItemName, currentLevel + 1);
      });
      result += `${indent}</${tagName}>${newline}`;
      return result;
    }
    
    if (typeof value === 'object') {
      let result = `${indent}<${tagName}>${newline}`;
      const keys = options.sortElements ? Object.keys(value).sort() : Object.keys(value);
      
      keys.forEach(key => {
        const sanitizedKey = sanitizeTagName(key);
        result += processValue(value[key], sanitizedKey, currentLevel + 1);
      });
      
      result += `${indent}</${tagName}>${newline}`;
      return result;
    }
    
    // Handle primitive values
    const textValue = String(value);
    const content = shouldUseCDATA(textValue, options.cdataThreshold)
      ? `<![CDATA[${textValue}]]>`
      : options.escapeText 
        ? escapeXml(textValue)
        : textValue;
    
    if (options.selfClosingTags && textValue === '') {
      return `${indent}<${tagName}/>${newline}`;
    }
    
    return `${indent}<${tagName}>${content}</${tagName}>${newline}`;
  };

  return processValue(data, options.rootElement, level);
};

const buildXmlDocument = (yamlData: any, options: ConversionOptions): string => {
  let xmlString = '';
  
  // Add XML declaration
  if (options.addXmlDeclaration) {
    xmlString += '<?xml version="1.0" encoding="UTF-8"?>';
    if (options.xmlFormat === 'pretty') xmlString += '\n';
  }
  
  // Convert data to XML
  const sanitizedRoot = sanitizeTagName(options.rootElement);
  xmlString += `<${sanitizedRoot}>`;
  if (options.xmlFormat === 'pretty') xmlString += '\n';
  
  // Process the actual data
  if (typeof yamlData === 'object' && yamlData !== null) {
    const keys = options.sortElements ? Object.keys(yamlData).sort() : Object.keys(yamlData);
    
    keys.forEach(key => {
      const sanitizedKey = sanitizeTagName(key);
      xmlString += convertToXml(yamlData[key], { ...options, rootElement: sanitizedKey }, 1);
    });
  } else {
    const content = shouldUseCDATA(String(yamlData), options.cdataThreshold)
      ? `<![CDATA[${yamlData}]]>`
      : options.escapeText 
        ? escapeXml(String(yamlData))
        : String(yamlData);
    xmlString += content;
  }
  
  if (options.xmlFormat === 'pretty' && !xmlString.endsWith('\n')) {
    xmlString += '\n';
  }
  xmlString += `</${sanitizedRoot}>`;
  
  return xmlString;
};

// ============= ANALYTICS DASHBOARD =============
const AnalyticsDashboard: React.FC = memo(() => {
  const [analytics, setAnalytics] = useState<AnalyticsEvent[]>([]);
  const { isDark } = useTheme();
  
  useEffect(() => {
    const data: AnalyticsEvent[] = JSON.parse(localStorage.getItem('yamlXmlConverter_analytics') || '[]');
    setAnalytics(data);
  }, []);
  
  const stats: AnalyticsStats = useMemo(() => {
    const total = analytics.length;
    const conversions = analytics.filter((a) => a.event === 'yaml_converted_to_xml').length;
    const validations = analytics.filter((a) => a.event === 'yaml_validated').length;
    
    return { total, conversions, validations };
  }, [analytics]);
  
  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Usage Analytics
      </h3>
      <div className="grid grid-cols-3 gap-4">
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {stats.total}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Total Events
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            {stats.conversions}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Conversions
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
            {stats.validations}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Validations
          </div>
        </div>
      </div>
    </div>
  );
});

AnalyticsDashboard.displayName = 'AnalyticsDashboard';

// ============= MAIN COMPONENT =============
const YamlToXmlContent: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { track } = useAnalytics();
  
  // Basic States
  const [yamlInput, setYamlInput] = useState<string>('');
  const [xmlOutput, setXmlOutput] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [fileName, setFileName] = useState<string>('yaml_data');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [isXmlGenerated, setIsXmlGenerated] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  
  // Advanced Options
  const [xmlFormat, setXmlFormat] = useState<XmlFormat>('pretty');
  const [indentLevel, setIndentLevel] = useState<number>(2);
  const [rootElement, setRootElement] = useState<string>('root');
  const [addXmlDeclaration, setAddXmlDeclaration] = useState<boolean>(true);
  const [selfClosingTags, setSelfClosingTags] = useState<boolean>(true);
  const [escapeText, setEscapeText] = useState<boolean>(true);
  const [sortElements, setSortElements] = useState<boolean>(false);
  const [arrayItemName, setArrayItemName] = useState<string>('item');
  const [cdataThreshold, setCdataThreshold] = useState<number>(100);
  const [validateOnType, setValidateOnType] = useState<boolean>(false);
  const [autoConvert, setAutoConvert] = useState<boolean>(false);

  // Event Handlers
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const value = e.target.value;
    setYamlInput(value);
    setError('');
    setIsXmlGenerated(false);
    
    if (validateOnType && value.trim()) {
      const [isValid, result] = validateYaml(value);
      if (!isValid) {
        setError(`YAML Validation: ${result instanceof Error ? result.message : String(result)}`);
      }
    }
    
    track('yaml_input_changed', { length: value.length });
  }, [validateOnType, track]);

  const useExampleData = useCallback((): void => {
    const exampleYaml = `# E-commerce Platform Configuration
version: "3.2.1"
environment: production

database:
  host: "postgres-primary.db.internal"
  port: 5432
  name: "ecommerce_prod"
  ssl_mode: "require"
  pool_size: 50

services:
  user_service:
    image: "user-service:v2.1.3"
    port: 8080
    replicas: 3
    environment:
      LOG_LEVEL: "info"
      DB_POOL: "20"
      JWT_EXPIRATION: "24h"
    features:
      - "new_registration"
      - "email_verification"
      - "social_login"
      
  product_service:
    image: "product-service:v1.8.2"
    port: 8081
    replicas: 5
    environment:
      LOG_LEVEL: "warn"
      CACHE_TTL: "3600"
      SEARCH_ENGINE: "elasticsearch"

external_apis:
  payment:
    stripe:
      api_url: "https://api.stripe.com/v1"
      supported_currencies:
        - "USD"
        - "EUR"
        - "GBP"
      features:
        subscriptions: true
        installments: true
        
security:
  authentication:
    jwt_secret: "\${JWT_SECRET}"
    token_expiration: "24h"
  cors:
    allowed_origins:
      - "https://app.company.com"
      - "https://admin.company.com"
    allowed_methods:
      - "GET"
      - "POST"
      - "PUT"
      - "DELETE"
    credentials: true`;

    setYamlInput(exampleYaml);
    setError('');
    setIsXmlGenerated(false);
    setXmlOutput('');
    track('example_data_used');
  }, [track]);

  const beautifyYaml = useCallback((): void => {
    try {
      if (!yamlInput.trim()) {
        setError('Please enter YAML data to format');
        return;
      }
      
      const [isValid, parsed] = validateYaml(yamlInput);
      
      if (!isValid) {
        setError(`Invalid YAML format: ${parsed instanceof Error ? parsed.message : String(parsed)}`);
        return;
      }
      
      const beautified = jsYaml.dump(parsed, {
        indent: 2,
        sortKeys: sortElements
      });
      
      setYamlInput(beautified);
      setError('');
      track('yaml_formatted');
    } catch (e) {
      setError(`Error formatting YAML: ${e instanceof Error ? e.message : String(e)}`);
      track('formatting_error', { error: e instanceof Error ? e.message : String(e) });
    }
  }, [yamlInput, sortElements, track]);

  const validateYamlOnly = useCallback((): void => {
    if (!yamlInput.trim()) {
      setError('Please enter YAML data to validate');
      return;
    }
    
    const [isValid, result] = validateYaml(yamlInput);
    
    if (isValid) {
      setError('✅ YAML is valid!');
      setTimeout(() => setError(''), 3000);
    } else {
      setError(`❌ Invalid YAML: ${result instanceof Error ? result.message : String(result)}`);
    }
    
    track('yaml_validated', { isValid });
  }, [yamlInput, track]);

  const convertYamlToXml = useCallback(async (): Promise<void> => {
    setError('');
    setIsLoading(true);
    setIsXmlGenerated(false);
    setXmlOutput('');
    
    if (!yamlInput.trim()) {
      setError('Please enter YAML data');
      setIsLoading(false);
      return;
    }

    try {
      const startTime = Date.now();
      
      const [isValid, parsedYaml] = validateYaml(yamlInput);
      
      if (!isValid) {
        setError(`Invalid YAML format: ${parsedYaml instanceof Error ? parsedYaml.message : String(parsedYaml)}`);
        setIsLoading(false);
        track('conversion_error', { error: 'invalid_yaml' });
        return;
      }
      
      const options: ConversionOptions = {
        xmlFormat,
        indentLevel,
        rootElement,
        addXmlDeclaration,
        selfClosingTags,
        escapeText,
        sortElements,
        arrayItemName,
        cdataThreshold
      };
      
      const xmlString = buildXmlDocument(parsedYaml, options);
      
      setXmlOutput(xmlString);
      setIsXmlGenerated(true);
      setIsLoading(false);
      
      const processingTime = Date.now() - startTime;
      
      track('yaml_converted_to_xml', {
        format: xmlFormat,
        processingTime,
        yamlLines: yamlInput.split('\n').length,
        xmlLength: xmlString.length,
        rootElement,
        selfClosingTags
      });
      
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      setError(`Error converting YAML to XML: ${errorMessage}`);
      setIsLoading(false);
      track('conversion_error', { error: errorMessage });
    }
  }, [yamlInput, xmlFormat, indentLevel, rootElement, addXmlDeclaration, selfClosingTags, escapeText, sortElements, arrayItemName, cdataThreshold, track]);

  const copyToClipboard = useCallback(async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(xmlOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      track('xml_copied');
    } catch (e) {
      setError('Failed to copy to clipboard');
      track('copy_error');
    }
  }, [xmlOutput, track]);

  const downloadXml = useCallback((): void => {
    const blob = new Blob([xmlOutput], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'yaml_data'}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    track('xml_downloaded', { fileName: `${fileName}.xml` });
  }, [xmlOutput, fileName, track]);

  const clearAll = useCallback((): void => {
    setYamlInput('');
    setXmlOutput('');
    setError('');
    setFileName('yaml_data');
    setRootElement('root');
    setIsXmlGenerated(false);
    setCopied(false);
    track('data_cleared');
  }, [track]);

  const toggleFullscreen = useCallback((): void => {
    setIsFullscreen(!isFullscreen);
    track('fullscreen_toggled', { isFullscreen: !isFullscreen });
  }, [isFullscreen, track]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    track('file_uploaded', { 
      fileName: file.name, 
      fileSize: file.size,
      fileType: file.type 
    });
    
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop();
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }
    
    const reader = new FileReader();
    reader.onload = (event): void => {
      if (event.target?.result) {
        setYamlInput(event.target.result as string);
        setError('');
        setIsXmlGenerated(false);
        setXmlOutput('');
      }
    };
    reader.onerror = (): void => {
      setError('Error reading the file');
      track('file_upload_error', { fileName: file.name });
    };
    reader.readAsText(file);
  }, [track]);

  // Auto-convert effect
  useEffect(() => {
    if (autoConvert && yamlInput.trim() && !error) {
      const timer = setTimeout(() => {
        convertYamlToXml();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [yamlInput, autoConvert, error, convertYamlToXml]);

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} ${isFullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}>
      
      {/* SEO Meta Tags */}
      <Helmet>
        <>
          <title>Free YAML to XML Converter Online - Convert YAML Files to XML Format</title>
          <meta name="description" content="Convert YAML data to XML format online for free. Advanced features include custom formatting, validation, self-closing tags, and CDATA support." />
          <meta name="keywords" content="YAML to XML converter, YAML converter, XML converter, online converter" />
          
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "YAML to XML Converter",
              "description": "Convert YAML data to XML format online for free",
              "applicationCategory": "UtilityApplication",
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            })}
          </script>
        </>
      </Helmet>

      <div className={`${isFullscreen ? 'p-4' : 'max-w-7xl mx-auto p-6'}`}>
        
        {/* Header with Controls */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              YAML to XML Converter
            </h1>
            <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Convert YAML data to XML format with advanced validation and formatting options
            </p>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Analytics"
              type="button"
            >
              📊
            </button>
            
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Theme"
              type="button"
            >
              {isDark ? '☀️' : '🌙'}
            </button>
            
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Fullscreen"
              type="button"
            >
              {isFullscreen ? '📱' : '🖥️'}
            </button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <div className="mb-6">
            <AnalyticsDashboard />
          </div>
        )}

        {/* Description */}
        <div className={`mb-6 p-4 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
          <p>
            Convert YAML data to XML format with advanced validation, formatting options, and real-time processing.
            Supports file uploads, syntax validation, self-closing tags, CDATA sections, and multiple output formats.
          </p>
        </div>
        
        <div className={`grid ${isFullscreen ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} gap-6`}>
          
          {/* Input Section */}
          <div className="flex flex-col h-full">
            <div className={`p-4 rounded-lg shadow-sm mb-4 flex-grow border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-center mb-3">
                <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  YAML Input
                </label>
                <div className="flex space-x-2">
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    onClick={useExampleData}
                    type="button"
                  >
                    Use Example
                  </button>
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    onClick={beautifyYaml}
                    type="button"
                  >
                    Format YAML
                  </button>
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-800'}`}
                    onClick={validateYamlOnly}
                    type="button"
                  >
                    Validate
                  </button>
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={() => setYamlInput('')}
                    type="button"
                  >
                    Clear
                  </button>
                </div>
              </div>
              
              {/* File Upload */}
              <div className="mb-4">
                <input
                  type="file"
                  accept=".yaml,.yml,.txt"
                  onChange={handleFileUpload}
                  className={`block w-full text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    ${isDark ? 'file:bg-blue-900 file:text-blue-200 hover:file:bg-blue-800' : 'file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100'}`}
                />
              </div>
              
              <textarea
                className={`w-full h-80 p-3 border rounded-lg font-mono text-sm transition-colors resize-none ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                value={yamlInput}
                onChange={handleInputChange}
                placeholder="Paste your YAML data here, e.g.,
name: John Doe
age: 30
address:
  city: New York
  zip: 10001
hobbies:
  - reading
  - hiking"
              />
            </div>
            
            {/* Configuration */}
            <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Output File Name
                  </label>
                  <input
                    type="text"
                    className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                    value={fileName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFileName(e.target.value)}
                    placeholder="Enter file name (without extension)"
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Root Element Name
                  </label>
                  <input
                    type="text"
                    className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                    value={rootElement}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRootElement(e.target.value)}
                    placeholder="Enter root element name"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  XML Format
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={xmlFormat}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setXmlFormat(e.target.value as XmlFormat)}
                >
                  <option value="pretty">Pretty (indented with line breaks)</option>
                  <option value="compact">Compact (minimal whitespace)</option>
                  <option value="minified">Minified (no whitespace)</option>
                </select>
              </div>
              
              <div className="mb-4">
                <button
                  className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  type="button"
                >
                  <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              
              {showAdvanced && (
                <div className={`space-y-4 p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  
                  {/* XML Formatting Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>XML Formatting</h4>
                    
                    {xmlFormat === 'pretty' && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Indentation: {indentLevel} spaces
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="8"
                          value={indentLevel}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIndentLevel(parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    )}
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Array Item Name
                      </label>
                      <input
                        type="text"
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        value={arrayItemName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setArrayItemName(e.target.value)}
                        placeholder="item"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="addXmlDeclaration"
                          checked={addXmlDeclaration}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddXmlDeclaration(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="addXmlDeclaration" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          XML declaration
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="selfClosingTags"
                          checked={selfClosingTags}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelfClosingTags(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="selfClosingTags" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Self-closing tags
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="escapeText"
                          checked={escapeText}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEscapeText(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="escapeText" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Escape XML chars
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="sortElements"
                          checked={sortElements}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSortElements(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="sortElements" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Sort elements
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* CDATA Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>CDATA Settings</h4>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        CDATA Threshold: {cdataThreshold} characters
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="500"
                        step="10"
                        value={cdataThreshold}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCdataThreshold(parseInt(e.target.value))}
                        className="w-full"
                      />
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Text longer than this will be wrapped in CDATA (0 = disabled)
                      </div>
                    </div>
                  </div>

                  {/* Processing Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Processing Options</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="validateOnType"
                          checked={validateOnType}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidateOnType(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="validateOnType" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Real-time validation
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="autoConvert"
                          checked={autoConvert}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutoConvert(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="autoConvert" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Auto-convert (1s delay)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-4 flex justify-between">
                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDark 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                  }`}
                  onClick={convertYamlToXml}
                  disabled={isLoading}
                  type="button"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </div>
                  ) : (
                    'Convert to XML'
                  )}
                </button>
                
                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDark 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 focus:ring-gray-500' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800 focus:ring-gray-500'
                  }`}
                  onClick={clearAll}
                  type="button"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
          
          {/* Output Section */}
          <div className={`p-4 rounded-lg shadow-sm h-full flex flex-col border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex justify-between items-center mb-3">
              <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                XML Output
              </label>
              
              {isXmlGenerated && (
                <div className="flex space-x-2">
                  <button
                    className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    onClick={copyToClipboard}
                    type="button"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {copied ? 'Copied!' : 'Copy XML'}
                  </button>
                  
                  <button
                    className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    onClick={downloadXml}
                    type="button"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex-grow flex flex-col">
              {error && (
                <div className={`border p-3 rounded-lg mb-3 ${
                  error.startsWith('✅') 
                    ? isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'
                    : isDark ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  <p className="font-medium">{error.startsWith('✅') ? 'Validation Result' : 'Error'}</p>
                  <p>{error}</p>
                </div>
              )}
              
              {!error && !isXmlGenerated && (
                <div className={`border p-4 rounded-lg flex-grow flex items-center justify-center ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                  <div className="text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <p>
                      XML output will appear here after conversion.
                    </p>
                  </div>
                </div>
              )}
              
              {!error && isXmlGenerated && (
                <div className="flex-grow flex flex-col">
                  <textarea
                    className={`w-full flex-grow p-3 border rounded-lg font-mono text-sm transition-colors resize-none ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                    value={xmlOutput}
                    readOnly
                  />
                  
                  {/* Success Message */}
                  <div className={`mt-4 border p-3 rounded-lg ${isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'}`}>
                    <div className="flex items-center mb-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <p className="font-medium">YAML Successfully Converted to XML!</p>
                    </div>
                    <div className={`text-sm space-y-1 ${isDark ? 'text-green-300' : 'text-green-600'}`}>
                      <p><strong>Format:</strong> {xmlFormat}</p>
                      <p><strong>Root Element:</strong> {rootElement}</p>
                      <p><strong>Output Size:</strong> {xmlOutput.length.toLocaleString()} characters</p>
                      <p><strong>Lines:</strong> {xmlOutput.split('\n').length.toLocaleString()}</p>
                      {selfClosingTags && <p><strong>Self-closing tags:</strong> Enabled</p>}
                      {cdataThreshold > 0 && <p><strong>CDATA threshold:</strong> {cdataThreshold} characters</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Information Section */}
        <div className={`mt-8 p-6 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>About YAML to XML Conversion</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>How This Converter Works</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                This tool transforms your YAML data into valid XML format with comprehensive validation and formatting options. 
                YAML (YAML Ain't Markup Language) is a human-friendly data serialization standard, while XML (eXtensible Markup Language) 
                is widely used for data interchange in various applications.
              </p>
            </div>
            
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Advanced Features</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Multiple XML Formats</strong> - Pretty, compact, and minified output options</li>
                <li><strong>Custom Root Elements</strong> - Specify your own root element name</li>
                <li><strong>Self-closing Tags</strong> - Modern XML syntax for empty elements</li>
                <li><strong>CDATA Support</strong> - Automatic CDATA wrapping for large text blocks</li>
                <li><strong>Real-time Validation</strong> - Live YAML syntax checking</li>
                <li><strong>File Upload Support</strong> - Direct YAML file import</li>
                <li><strong>Auto-conversion</strong> - Optional real-time conversion</li>
                <li><strong>Dark/Light Theme</strong> - Comfortable editing environment</li>
                <li><strong>Usage Analytics</strong> - Track conversion patterns</li>
              </ul>
            </div>
            
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Browser Compatibility</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                This converter works in all modern browsers. All processing happens locally in your browser for maximum privacy and speed.
                Generated XML output is fully compliant with XML 1.0 standards.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with ThemeProvider
const YamlToXml: React.FC = () => {
  return (
    <ThemeProvider>
      <YamlToXmlContent />
    </ThemeProvider>
  );
};

export default YamlToXml;